import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { getPunditPurohitAuthHeaders } from '../utils/auth';

const emptyForm = {
  type: 'Home',
  address: '',
  city: '',
  state: '',
  postalCode: '',
  isDefault: false,
};

const AddressBook = ({ embedded = false, onSelect, selectedAddressId }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const apiBase = process.env.REACT_APP_API_BASE_URL;

  const fetchAddresses = useCallback(() => {
    setLoading(true);
    axios
      .get(`${apiBase}/api/punditpurohit/addresses`, { headers: getPunditPurohitAuthHeaders() })
      .then((res) => setAddresses(res.data))
      .catch((err) => {
        console.error('Unable to fetch addresses', err);
        setAddresses([]);
      })
      .finally(() => setLoading(false));
  }, [apiBase]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const openModal = (address = null) => {
    if (address) {
      setEditingAddress(address);
      setFormData({
        type: address.type,
        address: address.address,
        city: address.city || '',
        state: address.state || '',
        postalCode: address.postal_code || '',
        isDefault: Boolean(address.isDefault),
      });
    } else {
      setEditingAddress(null);
      setFormData(emptyForm);
    }
    setIsModalOpen(true);
  };

  const handleSave = (e) => {
    e.preventDefault();
    const request = editingAddress
      ? axios.put(`${apiBase}/api/punditpurohit/addresses/${editingAddress.id}`, formData, { headers: getPunditPurohitAuthHeaders() })
      : axios.post(`${apiBase}/api/punditpurohit/addresses`, formData, { headers: getPunditPurohitAuthHeaders() });

    request
      .then(() => {
        setIsModalOpen(false);
        fetchAddresses();
      })
      .catch((err) => alert(err?.response?.data?.message || 'Unable to save address.'));
  };

  const handleDelete = (id) => {
    if (!window.confirm('Are you sure you want to remove this address?')) return;
    axios
      .delete(`${apiBase}/api/punditpurohit/addresses/${id}`, { headers: getPunditPurohitAuthHeaders() })
      .then(fetchAddresses)
      .catch((err) => alert(err?.response?.data?.message || 'Unable to remove address.'));
  };

  return (
    <div className={embedded ? 'address-page-container embedded' : 'address-page-container'}>
      <div className="address-header-flex">
        <div>
          <h2>Your Saved Locations</h2>
          <p className="subtitle">Where should the Pundit arrive for the ritual?</p>
        </div>
      </div>

      {loading ? (
        <p>Loading addresses...</p>
      ) : (
        <div className="address-grid">
          {addresses.map((addr) => (
            <div
              key={addr.id}
              className={`address-card ${selectedAddressId === addr.id ? 'active' : ''}`}
              onClick={() => onSelect?.(addr)}
            >
              <div className="card-top">
                <span className="addr-tag">{addr.type}</span>
                {Boolean(addr.isDefault) && <span className="default-pill">Default</span>}
              </div>
              <p className="address-text">{addr.address}</p>
              <div className="addr-actions">
                <button onClick={(e) => { e.stopPropagation(); openModal(addr); }}>Edit</button>
                <button className="delete" onClick={(e) => { e.stopPropagation(); handleDelete(addr.id); }}>Remove</button>
              </div>
            </div>
          ))}

          <div className="add-address-card" onClick={() => openModal()}>
            <span className="plus-icon">+</span>
            <p>Add New Location</p>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="address-modal">
            <h3>{editingAddress ? 'Edit Address' : 'Add New Address'}</h3>
            <form onSubmit={handleSave}>
              <div className="input-group">
                <label>Label</label>
                <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                  <option value="Home">Home</option>
                  <option value="Office">Office</option>
                  <option value="Temple">Temple</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="input-group">
                <label>Full Address</label>
                <textarea
                  required
                  placeholder="Enter complete address details..."
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="input-group">
                  <label>City</label>
                  <input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                </div>
                <div className="input-group">
                  <label>State</label>
                  <input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} />
                </div>
              </div>
              <div className="input-group">
                <label>Postal Code</label>
                <input value={formData.postalCode} onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })} />
              </div>
              <label className="auth-checkbox">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                />
                Make default address
              </label>
              <div className="modal-btns">
                <button type="button" className="cancel-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button type="submit" className="save-btn">Save Address</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressBook;
